

import { useState, useEffect } from "react";
import { useWorkspaceStore } from "@/stores";
import { Project } from "@/stores/taskStore";
import { useTaskStore } from "@/stores/taskStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  Folder,
  Check,
} from "lucide-react";

const COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#ef4444", "#f97316",
  "#eab308", "#22c55e", "#14b8a6", "#0ea5e9", "#64748b",
];

interface ProjectsMenuProps {
  onProjectChange?: (projectId: string) => void;
}

export function ProjectsMenu({ onProjectChange }: ProjectsMenuProps) {
  const { currentWorkspace } = useWorkspaceStore();
  const { projects, setProjects, selectedProjectId, setSelectedProjectId } = useTaskStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState<Project | null>(null);
  const [showDelete, setShowDelete] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const [createForm, setCreateForm] = useState({ name: "", description: "", color: COLORS[0] });
  const [editForm, setEditForm] = useState({ name: "", description: "", color: "" });

  useEffect(() => {
    if (currentWorkspace && isOpen) {
      loadProjects();
    }
  }, [currentWorkspace, isOpen]);

  const loadProjects = async () => {
    try {
      const data = await api.get<{ success: boolean; projects: Project[] }>(`/projects?workspaceId=${currentWorkspace?.id}`);
      if (data.success) {
        setProjects(data.projects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim() || !currentWorkspace) return;

    setIsLoading(true);
    try {
      const data = await api.post<{ success: boolean; project: Project; error?: string }>('/projects', { ...createForm, workspaceId: currentWorkspace.id });
      
      if (data.success) {
        setProjects([...projects, data.project]);
        setSelectedProjectId(data.project.id);
        onProjectChange?.(data.project.id);
        setShowCreate(false);
        setCreateForm({ name: "", description: "", color: COLORS[0] });
        toast.success("Proyecto creado");
      } else {
        toast.error(data.error || "Error al crear");
      }
    } catch {
      toast.error("Error al crear proyecto");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.name.trim() || !showEdit) return;

    setIsLoading(true);
    try {
      const data = await api.put<{ success: boolean; project: Project; error?: string }>('/projects', {
        id: showEdit.id,
        name: editForm.name,
        description: editForm.description,
        color: editForm.color,
      });
      
      if (data.success) {
        setProjects(projects.map(p => p.id === showEdit.id ? data.project : p));
        setShowEdit(null);
        toast.success("Proyecto actualizado");
      } else {
        toast.error(data.error || "Error al actualizar");
      }
    } catch {
      toast.error("Error al actualizar proyecto");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!showDelete) return;

    setIsLoading(true);
    try {
      const data = await api.delete<{ success: boolean; error?: string }>(`/projects?id=${showDelete.id}`);
      
      if (data.success) {
        const newProjects = projects.filter(p => p.id !== showDelete.id);
        setProjects(newProjects);
        if (selectedProjectId === showDelete.id) {
          setSelectedProjectId(newProjects[0]?.id || null);
          onProjectChange?.(newProjects[0]?.id || "");
        }
        setShowDelete(null);
        toast.success("Proyecto eliminado");
      } else {
        toast.error(data.error || "Error al eliminar");
      }
    } catch {
      toast.error("Error al eliminar proyecto");
    } finally {
      setIsLoading(false);
    }
  };

  const selectProject = (project: Project) => {
    setSelectedProjectId(project.id);
    onProjectChange?.(project.id);
    setIsOpen(false);
  };

  const openEdit = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditForm({
      name: project.name,
      description: project.description || "",
      color: project.color || COLORS[0],
    });
    setShowEdit(project);
    setIsOpen(false);
  };

  const openDelete = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDelete(project);
    setIsOpen(false);
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
        >
          <div className="flex items-center gap-2">
            <Folder className="w-4 h-4" />
            <span className="truncate">
              {selectedProject?.name || "Proyectos"}
            </span>
          </div>
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => selectProject(project)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-muted transition-colors",
                    project.id === selectedProjectId && "bg-muted"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: project.color || "#6366f1" }}
                    />
                    <span className="truncate">{project.name}</span>
                    {project._count?.tasks !== undefined && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {project._count.tasks}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => openEdit(project, e)}
                      className="p-1 hover:bg-muted-foreground/20 rounded"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => openDelete(project, e)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              
              {projects.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No hay proyectos
                </div>
              )}
            </div>
            
            <div className="border-t p-2">
              <button
                onClick={() => { setShowCreate(true); setIsOpen(false); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nuevo Proyecto
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Nuevo Proyecto</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={createForm.name}
                    onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Nombre del proyecto"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={createForm.description}
                    onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Descripción opcional"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={cn(
                          "w-8 h-8 rounded-full transition-transform",
                          createForm.color === color ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-110"
                        )}
                        style={{ backgroundColor: color }}
                        onClick={() => setCreateForm({ ...createForm, color })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading || !createForm.name.trim()}>
                  {isLoading ? "Creando..." : "Crear"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEdit(null)}>
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Editar Proyecto</h2>
            <form onSubmit={handleEdit}>
              <div className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Nombre del proyecto"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={editForm.description}
                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Descripción opcional"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={cn(
                          "w-8 h-8 rounded-full transition-transform",
                          editForm.color === color ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-110"
                        )}
                        style={{ backgroundColor: color }}
                        onClick={() => setEditForm({ ...editForm, color })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowEdit(null)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading || !editForm.name.trim()}>
                  {isLoading ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDelete(null)}>
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-2">Eliminar Proyecto</h2>
            <p className="text-muted-foreground mb-4">
              ¿Estás seguro de eliminar "{showDelete.name}"? Se eliminarán todas las tareas asociadas. Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDelete(null)}>
                Cancelar
              </Button>
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
                {isLoading ? "Eliminando..." : "Eliminar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
